import { create } from 'zustand';
import type { ContextMenuState } from '../core/ui/ContextMenu';
import type { NotificationData } from '../components/Notification';

interface UIState {
  view: 'large' | 'small';
  hue: number;
  baseHue: number;
  contrast: number;
  saturation: number;
  vibrant: boolean;
  lfoActive: boolean;
  lfoSpeed: number;
  lfoShape: 'sine' | 'triangle' | 'square';
  showLfoControls: boolean;
  gradientMode: boolean;
  showGradientControls: boolean;
  mobileView: 'dms' | 'chat' | 'vaults' | 'lobby';
  showDMsSidebar: boolean;
  showRoomsSidebar: boolean;
  activeTab: 'chat' | 'feed';
  bgTheme: 'synthwave' | 'aurora' | 'day' | 'ocean' | 'classic';
  showUiConfig: boolean;
  showMobileVaults: boolean;
  isTopBarExpanded: boolean;
  isTopBarPinned: boolean;
  showUserSettings: boolean;
  sidebarView: 'dms' | 'channels';
  activeSettingsCategory: string;
  showSidebarsInRoom: boolean;
  notifications: NotificationData[];
  adminModeEnabled: boolean;
  showCreateVaultModal: boolean;
  isEditingVault: boolean;
  showCreateChannelModal: boolean;
  showMembersModal: boolean;
  showClearNodesConfirm: boolean;
  showVaultDropdown: boolean;
  showPasswordModal: boolean;
  showSendMessageModal: boolean;
  viewingProfileId: string | null;
  viewingImageUrl: string | null;
  contextMenu: ContextMenuState;

  // Actions
  setView: (view: 'large' | 'small') => void;
  setHue: (hue: number) => void;
  setBaseHue: (baseHue: number) => void;
  setContrast: (contrast: number) => void;
  setSaturation: (saturation: number) => void;
  setVibrant: (vibrant: boolean) => void;
  setLfoActive: (active: boolean) => void;
  setLfoSpeed: (speed: number) => void;
  setLfoShape: (shape: 'sine' | 'triangle' | 'square') => void;
  setShowLfoControls: (show: boolean) => void;
  setGradientMode: (mode: boolean) => void;
  setShowGradientControls: (show: boolean) => void;
  setMobileView: (view: 'dms' | 'chat' | 'vaults' | 'lobby') => void;
  setShowDMsSidebar: (show: boolean) => void;
  setShowRoomsSidebar: (show: boolean) => void;
  setActiveTab: (tab: 'chat' | 'feed') => void;
  setBgTheme: (theme: 'synthwave' | 'aurora' | 'day' | 'ocean' | 'classic') => void;
  setShowUiConfig: (show: boolean) => void;
  setShowMobileVaults: (show: boolean) => void;
  setIsTopBarExpanded: (expanded: boolean) => void;
  setIsTopBarPinned: (pinned: boolean) => void;
  setShowUserSettings: (show: boolean) => void;
  setSidebarView: (view: 'dms' | 'channels') => void;
  setActiveSettingsCategory: (category: string) => void;
  setShowSidebarsInRoom: (show: boolean) => void;
  setNotifications: (notifications: NotificationData[] | ((prev: NotificationData[]) => NotificationData[])) => void;
  setAdminModeEnabled: (enabled: boolean) => void;
  setShowCreateVaultModal: (show: boolean) => void;
  setIsEditingVault: (editing: boolean) => void;
  setShowCreateChannelModal: (show: boolean) => void;
  setShowMembersModal: (show: boolean) => void;
  setShowClearNodesConfirm: (show: boolean) => void;
  setShowVaultDropdown: (show: boolean) => void;
  setShowPasswordModal: (show: boolean) => void;
  setShowSendMessageModal: (show: boolean) => void;
  setViewingProfileId: (id: string | null) => void;
  setViewingImageUrl: (url: string | null) => void;
  setContextMenu: (menu: ContextMenuState | ((prev: ContextMenuState) => ContextMenuState)) => void;
}

export const useUIStore = create<UIState>((set) => ({
  view: 'large',
  hue: 175,
  baseHue: 175,
  contrast: 103,
  saturation: 148,
  vibrant: false,
  lfoActive: false,
  lfoSpeed: 1,
  lfoShape: 'sine',
  showLfoControls: false,
  gradientMode: false,
  showGradientControls: false,
  mobileView: 'lobby',
  showDMsSidebar: true,
  showRoomsSidebar: true,
  activeTab: 'chat',
  bgTheme: 'classic',
  showUiConfig: false,
  showMobileVaults: false,
  isTopBarExpanded: false,
  isTopBarPinned: true,
  showUserSettings: false,
  sidebarView: 'channels',
  activeSettingsCategory: 'Account',
  showSidebarsInRoom: false,
  notifications: [],
  adminModeEnabled: false,
  showCreateVaultModal: false,
  isEditingVault: false,
  showCreateChannelModal: false,
  showMembersModal: false,
  showClearNodesConfirm: false,
  showVaultDropdown: false,
  showPasswordModal: false,
  showSendMessageModal: false,
  viewingProfileId: null,
  viewingImageUrl: null,
  contextMenu: {
    x: 0,
    y: 0,
    type: null,
    targetId: '',
    visible: false
  },

  setView: (view) => set({ view }),
  setHue: (hue) => set({ hue }),
  setBaseHue: (baseHue) => set({ baseHue }),
  setContrast: (contrast) => set({ contrast }),
  setSaturation: (saturation) => set({ saturation }),
  setVibrant: (vibrant) => set({ vibrant }),
  setLfoActive: (lfoActive) => set({ lfoActive }),
  setLfoSpeed: (lfoSpeed) => set({ lfoSpeed }),
  setLfoShape: (lfoShape) => set({ lfoShape }),
  setShowLfoControls: (showLfoControls) => set({ showLfoControls }),
  setGradientMode: (gradientMode) => set({ gradientMode }),
  setShowGradientControls: (showGradientControls) => set({ showGradientControls }),
  setMobileView: (mobileView) => set({ mobileView }),
  setShowDMsSidebar: (showDMsSidebar) => set({ showDMsSidebar }),
  setShowRoomsSidebar: (showRoomsSidebar) => set({ showRoomsSidebar }),
  setActiveTab: (activeTab) => set({ activeTab }),
  setBgTheme: (bgTheme) => set({ bgTheme }),
  setShowUiConfig: (showUiConfig) => set({ showUiConfig }),
  setShowMobileVaults: (showMobileVaults) => set({ showMobileVaults }),
  setIsTopBarExpanded: (isTopBarExpanded) => set({ isTopBarExpanded }),
  setIsTopBarPinned: (isTopBarPinned) => set({ isTopBarPinned }),
  setShowUserSettings: (showUserSettings) => set({ showUserSettings }),
  setSidebarView: (sidebarView) => set({ sidebarView }),
  setActiveSettingsCategory: (activeSettingsCategory) => set({ activeSettingsCategory }),
  setShowSidebarsInRoom: (showSidebarsInRoom) => set({ showSidebarsInRoom }),
  setNotifications: (updater) => set((state) => ({
    notifications: typeof updater === 'function' ? updater(state.notifications) : updater
  })),
  setAdminModeEnabled: (adminModeEnabled) => set({ adminModeEnabled }),
  setShowCreateVaultModal: (showCreateVaultModal) => set({ showCreateVaultModal }),
  setIsEditingVault: (isEditingVault) => set({ isEditingVault }),
  setShowCreateChannelModal: (showCreateChannelModal) => set({ showCreateChannelModal }),
  setShowMembersModal: (showMembersModal) => set({ showMembersModal }),
  setShowClearNodesConfirm: (showClearNodesConfirm) => set({ showClearNodesConfirm }),
  setShowVaultDropdown: (showVaultDropdown) => set({ showVaultDropdown }),
  setShowPasswordModal: (showPasswordModal) => set({ showPasswordModal }),
  setShowSendMessageModal: (showSendMessageModal) => set({ showSendMessageModal }),
  setViewingProfileId: (viewingProfileId) => set({ viewingProfileId }),
  setViewingImageUrl: (viewingImageUrl) => set({ viewingImageUrl }),
  setContextMenu: (updater) => set((state) => ({
    contextMenu: typeof updater === 'function' ? updater(state.contextMenu) : updater
  }))
}));
